import { useState } from 'react';
import { Link } from 'react-router-dom';

interface ConsentOptions {
  analytics: boolean;
  personalizedAds: boolean;
}

interface PrivacyConsentBannerProps {
  onAcceptAll: () => void;
  onRejectNonEssential: () => void;
  onSaveCustom: (options: ConsentOptions) => void;
}

export default function PrivacyConsentBanner({
  onAcceptAll,
  onRejectNonEssential,
  onSaveCustom,
}: PrivacyConsentBannerProps): JSX.Element {
  const [isManaging, setIsManaging] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [personalizedAds, setPersonalizedAds] = useState(false);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-settings-title"
      className="fixed bottom-4 right-4 z-[70] w-[420px] max-w-[calc(100vw-2rem)]"
    >
      <div className="rounded-2xl border border-dark-200 bg-white shadow-2xl shadow-dark-900/15">
        <div className="p-5 sm:p-6">
          <h2 id="privacy-settings-title" className="text-xl sm:text-2xl font-bold text-dark-900">
            Privacy Settings
          </h2>
          <p className="mt-3 text-sm sm:text-base text-dark-600 leading-relaxed">
            We use cookies to improve your experience. You can choose which cookies you accept. See our <Link to="/privacy" className="text-primary-600 hover:text-primary-700 font-semibold">Privacy Policy</Link> for details.
          </p>

          {isManaging && (
            <div className="mt-4 rounded-xl border border-dark-200 bg-dark-50 p-4">
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-dark-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-700">
                    Analytics cookies to help us improve performance and experience
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={personalizedAds}
                    onChange={(e) => setPersonalizedAds(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-dark-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-700">
                    Personalised advertising cookies for targeted adverts
                  </span>
                </label>
              </div>
              <button
                type="button"
                onClick={() => onSaveCustom({ analytics, personalizedAds })}
                className="mt-4 inline-flex items-center justify-center rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold px-4 py-2.5 transition-colors"
              >
                Save my choices
              </button>
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setIsManaging((prev) => !prev)}
              className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg border border-dark-300 text-dark-700 hover:bg-dark-50 text-sm font-semibold transition-colors"
            >
              Manage options
            </button>
            <button
              type="button"
              onClick={onRejectNonEssential}
              className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg border border-dark-300 text-dark-700 hover:bg-dark-50 text-sm font-semibold transition-colors"
            >
              Only essential
            </button>
            <button
              type="button"
              onClick={onAcceptAll}
              className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold transition-colors"
            >
              Accept all cookies
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
